import { isPaidSubscription, type SubscriptionLike } from '@/lib/quota/isPaidSubscription';

export type StoreProvider = 'app_store' | 'google_play';

const ANDROID_PACKAGE = 'live.autoclipper.app';

/** The store billing a subscription, or null (Stripe, PIX, grants, free). */
export function storeProviderOf(
  subscription: SubscriptionLike | null | undefined,
): StoreProvider | null {
  const provider = subscription?.payment_provider;
  return provider === 'app_store' || provider === 'google_play' ? provider : null;
}

/** A store provider only counts while the plan is paid: churned rows keep it. */
export function isLiveStorePlan(subscription: SubscriptionLike | null | undefined): boolean {
  return storeProviderOf(subscription) !== null && isPaidSubscription(subscription);
}

/** Where the store lets the user cancel or change the subscription. */
export function storeManageUrl(provider: StoreProvider, productId?: string | null): string {
  if (provider === 'app_store') return 'https://apps.apple.com/account/subscriptions';
  const sku = productId ? `sku=${encodeURIComponent(productId)}&` : '';
  return `https://play.google.com/store/account/subscriptions?${sku}package=${ANDROID_PACKAGE}`;
}

/** Display name of the store, for user-facing copy. */
export function storeDisplayName(provider: StoreProvider): string {
  return provider === 'app_store' ? 'App Store' : 'Google Play';
}

/** Web checkout endpoints answer HTTP 400 `{ error: 'store_managed' }` for a live store plan. */
export function isStoreManagedError(error: unknown): boolean {
  const e = error as { response?: { data?: { error?: string } }; message?: string } | null;
  return e?.response?.data?.error === 'store_managed' || Boolean(e?.message?.includes('store_managed'));
}
